import axios from 'axios';

const state = {
    worktimeData: {},
    loading: false,
    error: null,
};

const mutations = {
    SET_LOADING(state, loading) {
        state.loading = loading;
    },

    SET_ERROR(state, error) {
        state.error = error;
    },

    SET_WORKTIME_DATA(state, { key, data }) {
        state.worktimeData = {
            ...state.worktimeData,
            [key]: data,
        };
    },

    CLEAR_WORKTIME_DATA(state) {
        state.worktimeData = {};
    },
};

const actions = {
    async fetchWorktimeForDriver({ commit, state }, { driverNumber, rangeStartAt, rangeEndAt }) {
        const key = `driver_${driverNumber}_${rangeStartAt}_${rangeEndAt}`;

        if (state.worktimeData[key]) {
            return state.worktimeData[key];
        }

        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const queryData = {
                rangeStartAt,
                rangeEndAt,
            };

            const [dailyResult, allWorkTimesResult] = await Promise.all([
                axios.post(
                    `${import.meta.env.VITE_API_BASE_URL}/cpanel/work_hours/daily/driver/${driverNumber}`,
                    queryData,
                    { withCredentials: true },
                ),
                axios.post(`${import.meta.env.VITE_API_BASE_URL}/cpanel/work_hours`, queryData, {
                    withCredentials: true,
                }),
            ]);

            const worktimeData = {
                daily: dailyResult.data,
                all: allWorkTimesResult.data,
            };

            commit('SET_WORKTIME_DATA', { key, data: worktimeData });
            return worktimeData;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Error fetching worktime data:', error);
            return null;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async fetchWorktimeForDateRange({ commit, state }, { rangeStartAt, rangeEndAt }) {
        const key = `range_${rangeStartAt}_${rangeEndAt}`;

        if (state.worktimeData[key]) {
            return state.worktimeData[key];
        }

        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const queryData = {
                rangeStartAt,
                rangeEndAt,
            };

            const result = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/work_hours`,
                queryData,
                { withCredentials: true },
            );

            const worktimeData = result.data;
            commit('SET_WORKTIME_DATA', { key, data: worktimeData });
            return worktimeData;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Error fetching worktime data:', error);
            return null;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    calculateWorktimeFromShifts({}, shifts) {
        if (!shifts || !Array.isArray(shifts)) {
            return 0;
        }

        let totalWorkTime = 0;
        shifts.forEach((shift) => {
            if (shift.startAt && shift.endAt) {
                const start = new Date(shift.startAt);
                const end = new Date(shift.endAt);
                const hours = (end - start) / (1000 * 60 * 60);
                totalWorkTime += hours;
            }
        });

        return Math.round(totalWorkTime * 100) / 100;
    },

    getWorktimeForDriver({ state }, { driverNumber, rangeStartAt, rangeEndAt }) {
        const key = `driver_${driverNumber}_${rangeStartAt}_${rangeEndAt}`;
        const data = state.worktimeData[key];

        if (!data || !data.daily) {
            return null;
        }

        const driverWorktime = data.daily.reduce((total, workTime) => {
            return total + (workTime.workTime || 0);
        }, 0);

        return driverWorktime / 3600;
    },
};

const getters = {
    getWorktimeData: (state) => (key) => {
        return state.worktimeData[key] || null;
    },

    isLoading: (state) => state.loading,
    getError: (state) => state.error,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
